import React from 'react'
import { useDropzone } from 'react-dropzone'
import { IFile } from '@api'
import { pluralize } from '@utils'
import { LinkButton, Toaster } from '@ui'
import { Spinner } from '@blueprintjs/core'
import css from './fileDrop.module.scss'

interface Props {
  onDrop: (files: any[]) => void
  files?: IFile[]
  accept?: { [key: string]: string[] }
  maxSize?: number
  height?: number
}

const MB = 1e6

export default function Dropzone({
  onDrop,
  files = [],
  accept = { 'text/csv': ['.csv'] },
  maxSize = 11,
  height,
}: Props) {
  const [loading, setLoading] = React.useState(false)
  const readFiles: any[] = []

  const readFilesOnDrop = (files: any[], rejected: any[]) => {
    setLoading(true)
    return new Promise((resolve, reject) => {
      const onloadend =
        (f: IFile, reader: FileReader, resolve: (value?: unknown) => void) => () => {
          readFiles.push({
            name: f.name,
            type: f.type,
            content: reader.result as string,
          })
          if (readFiles.length !== files.length) return
          resolve()
        }
      files.forEach((f) => {
        const reader = new FileReader()
        reader.onload = onloadend(f, reader, resolve)

        reader.readAsDataURL(f)
      })

      for (const file of rejected) {
        if (file?.file?.size > maxSize * 1000000) {
          Toaster.fail({
            message: `Could not attach the file ${file?.file?.name}. File exceeds the size limit of ${maxSize} MB`,
          })
          continue
        }
        Toaster.fail({
          message: `Could not attach file ${file?.file?.name}. Wrong extension.`,
        })
      }
    }).then(() => {
      setLoading(false)
      return onDrop(readFiles)
    })
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: readFilesOnDrop,
    accept,
    maxSize: maxSize * MB,
  })

  return (
    <div
      className={css.style}
      {...getRootProps()}
      style={{
        border: isDragActive ? '2px dashed var(--border-color)' : '2px solid #fbfbfb',
        height,
      }}
    >
      <input {...getInputProps()} />
      <>
        {loading ? <Spinner size={32} /> : <i className={`fal fa-cloud-upload ${css.icon}`} />}
        {files.length ? (
          <>
            <p>
              File{pluralize(files.length)}: {files.map((f) => f.name).join(', ')}
            </p>
            <p className={css.subtext}>Click to select new file</p>
          </>
        ) : (
          <p>
            Drag and Drop <br /> or <LinkButton type="button" text="browse" />
          </p>
        )}
      </>
    </div>
  )
}
